import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route, Switch } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { OnboardingContactInformationSchema, OnboardingStepKey } from '@cbgms/api'
import { SignUpForm } from './SignUpForm'
import AlreadyRegisteredUserEmail from './AlreadyRegisteredUserEmail'
import { useDispatch } from 'react-redux'
import { onboardingSelfSignUp, setOnboardingToken } from 'app/auth/state/actions'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'

interface UserCredentials {
  EmailAddress: string
  Name: string
  FirstName: string
  NamePrefix: string
}
interface SignUpCredentialsProps {
  basePath: string
  onSubmit: (v: UserCredentials) => void
}

interface StepInputProps {
  basePath: string
  shopContactInformation: OnboardingContactInformationSchema | null
}

export interface stepOutputProps {
  userCredentials?: UserCredentials
}

export const useSignUpCredentialsStep: UseStepFunc<SignUpCredentialsProps, StepInputProps, stepOutputProps> = ({
  basePath,
  shopContactInformation,
  onboardingInfo
}) => {
  const stepName = OnboardingStepKey.SignUpCredentials
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const dispatch = useDispatch()
  const [userCredentials, setUserCredentials] = useState<UserCredentials | null>(null)
  const onStepSubmit = useCallback(
    (v: UserCredentials) => {
      setUserCredentials(v)
    },
    [setUserCredentials]
  )
  const complete = useCompleteOnSubmit(onStepSubmit)

  // //onboarding/create-company // create the actual company, returns token that can be used to create order accounts
  // /third-party-licenses/haynes // link up existing wholesaler for MG id

  const onPersist = useCallback(async () => {
    if (userCredentials) {
      const result = await dispatch(
        onboardingSelfSignUp({
          Shop: shopContactInformation!,
          Modules: onboardingInfo.onboardingModules,
          Owner: {
            EmailAddress: userCredentials.EmailAddress,
            FirstName: userCredentials.FirstName,
            NamePrefix: userCredentials.NamePrefix,
            Name: userCredentials.Name
          }
        })
      )
      if (result?.Data?.AuthenticationResult?.IDToken) {
        // set a temporary token, so the next api requests have a token they can use.
        dispatch(setOnboardingToken(result.Data.AuthenticationResult))
      }
    }
  }, [userCredentials, shopContactInformation])

  return {
    Component: SignUpCredentialsStep,
    CTAComponent: null,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: true,
    showStepperHeader: false,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    hideSkip: true,
    stepProps: {
      duplicationCheck: stepData.DuplicationCheck || [],
      basePath,
      onSubmit: complete.onSubmit
    },
    persister: { onPersist, persistOrder: 1 },
    userCredentials: userCredentials || undefined
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_finish_sign-up')}</>
}

export const SignUpCredentialsStep: React.FC<defaultStepFormProps & SignUpCredentialsProps> = ({ basePath, ...rest }) => {
  return (
    <Switch>
      <Route path={routing.pathAlreadyRegisteredUserEmail(basePath)} component={AlreadyRegisteredUserEmail} />
      <Route path={routing.pathSignUp(basePath)} render={() => <SignUpForm basePath={basePath} {...rest} />} />
    </Switch>
  )
}
